import type { Data } from "schema/agent";
import type { AgentFiles } from "@/features/agent";
import { combineCode, harnessRaw, prepareBootstrapCode } from "@/features/preview";
import iframeRaw from "./iframe.raw.html?raw";
import workerRaw from "./worker.raw.js?raw";

export interface ExportConfig {
  files: AgentFiles;
  width: number;
  height: number;
  duration: number;
  fps?: number;
  onProgress: (progress: number) => void;
  assetUrlMap?: Record<string, string>;
  data?: Data;
}

export interface ExportCapabilityResult {
  supported: boolean;
  reason?: "missing_api" | "unknown";
  isMobile: boolean;
}

export function checkExportCapability(): ExportCapabilityResult {
  const isMobile =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent));

  // Check for WebCodecs API support
  const hasVideoEncoder = typeof VideoEncoder === "function";
  const hasVideoFrame = typeof VideoFrame === "function";

  if (!hasVideoEncoder || !hasVideoFrame) {
    return {
      supported: false,
      reason: "missing_api",
      isMobile,
    };
  }

  return { supported: true, isMobile };
}

function createWorkerUrl(): string {
  const workerBlob = new Blob([workerRaw], { type: "application/javascript" });
  return URL.createObjectURL(workerBlob);
}

function createIframeContent(
  workerUrl: string,
  files: AgentFiles,
  width: number,
  height: number,
  duration: number,
  fps: number,
  assetUrlMap: Record<string, string>,
  data: Data,
): string {
  // Inject preview runtime scripts using shared helper
  const bootstrap = prepareBootstrapCode(JSON.stringify(assetUrlMap), JSON.stringify(data));

  // For export, we don't replace __WIDTH__/__HEIGHT__ in harness
  // because the iframe sets canvas dimensions manually
  const harness = harnessRaw;

  return iframeRaw
    .replace("__BOOTSTRAP_SCRIPT__", bootstrap)
    .replace("__HARNESS_SCRIPT__", harness)
    .replace(/__WIDTH__/g, String(width))
    .replace(/__HEIGHT__/g, String(height))
    .replace("__WORKER_URL__", workerUrl)
    .replace("__USER_JS__", JSON.stringify(combineCode(files)))
    .replace("__DURATION__", String(duration))
    .replace("__FPS__", String(fps));
}

export const renderAndExportVideo = (
  files: AgentFiles,
  width: number,
  height: number,
  duration: number,
  fps: number = 30,
  onProgress: (progress: number) => void,
  assetUrlMap: Record<string, string> = {},
  data: Data = {},
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    // Create a hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.width = `${width}px`;
    iframe.style.height = `${height}px`;
    iframe.style.visibility = "hidden";
    iframe.style.position = "absolute";
    iframe.style.top = "-9999px";
    iframe.style.left = "-9999px";
    document.body.appendChild(iframe);

    // Cleanup function
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    };

    // Listen for messages from the iframe
    const messageHandler = (event: MessageEvent) => {
      const { type, payload } = event.data;

      if (type === "EXPORT_PROGRESS") {
        onProgress(payload);
      } else if (type === "EXPORT_COMPLETE") {
        const blob = payload;
        cleanup();
        window.removeEventListener("message", messageHandler);
        resolve(blob);
      } else if (type === "EXPORT_ERROR") {
        cleanup();
        window.removeEventListener("message", messageHandler);
        reject(new Error(payload));
      }
    };

    window.addEventListener("message", messageHandler);

    const workerUrl = createWorkerUrl();
    const iframeContent = createIframeContent(
      workerUrl,
      files,
      width,
      height,
      duration,
      fps,
      assetUrlMap,
      data,
    );

    iframe.srcdoc = iframeContent;
  });
};
